import React, { useState, useEffect } from "react";
import {
  ArrowRightIcon,
  CogIcon,
  GiftIcon,
  PuzzlePieceIcon,
  ShoppingCartIcon,
  TrophyIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  PlayIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  CreditCardIcon,
  BanknotesIcon,
} from "@heroicons/react/24/outline";
import { LoaderFunction, LoaderFunctionArgs, MetaFunction, LinksFunction, redirect, json } from "@remix-run/node";
import { useLoaderData, Link, useNavigate } from "@remix-run/react";
import { useI18n } from '~/i18n/i18n';
import { getSeoMeta } from "~/utils/seo-meta";
import { LandingPage, LANDING_META, landingLinks } from "./landing";
import { KpiCard, DailyPlaysChart, ChannelSplitCards } from "../components/dashboard";
import { getMiniPopGamePlaysStats, getUpsellRevenueStats, getOrderStats, getGameTypeDistribution, getTopClaimedRewards, getConversionStats } from "~/utils/firestore.server";
import { findDocumentByField, updateDocument } from "~/utils/firestore.server";
import { getShopCurrencyCode } from "~/utils/shopify";
import { getEmailTrackingStats } from "~/utils/sendgrid-webhook";
import { PerformanceCache, getCachedClientData } from "~/utils/cache.server";
import { getUserSession } from "~/utils/session.server";
import { isAdminUser } from "~/utils/admin";
import CacheMonitor from "~/components/CacheMonitor";

// Asset imports
import imgGroup from "../assets/icons/game.svg";
import imgGift3 from "../assets/icons/gift-outline.svg";
import imgGift1Alt from "../assets/icons/gift-1.svg";
import imgMail from "../assets/icons/mail.svg";
import imgDollarSquare from "../assets/icons/dollar-square.svg";
import imgCoin from "../assets/icons/coin.svg";
import { recordDashboardSeen } from "~/utils/user-activity.server";
import { timestampToDate } from "~/utils/firestore.server";
import { trackEvent, buildMerchantProperties } from "~/utils/posthog.server";

// Logged-out visitors (and search crawlers) see the marketing landing at "/".
// Canonical points at "/" so it consolidates with /landing.
export const meta: MetaFunction = () => getSeoMeta({ ...LANDING_META, url: "/" });

export const links: LinksFunction = landingLinks;

export const loader: LoaderFunction = async ({ request }: LoaderFunctionArgs) => {
  const __t0 = Date.now();
  const url = new URL(request.url);
  const shop = url.searchParams.get("shop");
  const hmac = url.searchParams.get("hmac");
  const session = url.searchParams.get("session");

  if(shop && hmac && !session) {
    return redirect(`/api/install?shop=${shop}`);
  }

  try {
    // Get user from session
    const user = await getUserSession(request);
    const userEmail = user?.email || null;
    let userName = user?.display_name || user?.name || "User";

    // Debug session data

    // Check onboarding status server-side (skip for admin users)
    let showInstallationPopup = false;
    let showBillingConfirmationPopup = false;
    let confirmationUrl = null;
    let clientDoc = null;

    if (userEmail) {
      if (!isAdminUser(userEmail)) {
        try {
          clientDoc = await findDocumentByField('clients', 'email', userEmail);
          if (!clientDoc) {
            return redirect('/onboarding');
          }

          // Check if user has completed onboarding OR has existing data (for old users)
          const hasCompletedOnboarding = clientDoc.onboardingCompleted;
          const hasExistingData = clientDoc.totalPlays > 0 || clientDoc.shop || clientDoc.selectedGame;

          if (!hasCompletedOnboarding && !hasExistingData) {
            return redirect('/onboarding');
          } else if (!hasCompletedOnboarding && hasExistingData) {
          }

          // Check for pending billing confirmation
          if (clientDoc.confirmationUrl) {
            showBillingConfirmationPopup = true;
            confirmationUrl = clientDoc.confirmationUrl;
          }

          // Check if user is inactive and doesn't have subscription ID
          const isInactive = clientDoc.status !== "active";
          const hasNoSubscription = !clientDoc.subscriptionId;

          if (isInactive || hasNoSubscription) {
            showInstallationPopup = true;
          }
        } catch (error) {
          return redirect('/onboarding');
        }
      } else {
      }
    }

    // Resolve the store's currency for native revenue display. Backfill it once
    // for stores connected before currency capture existed (avoids forcing a reconnect).
    let currencyCode: string = (clientDoc as any)?.currencyCode || 'USD';
    if (
      clientDoc &&
      !(clientDoc as any).currencyCode &&
      (clientDoc as any).shop &&
      (clientDoc as any).accessToken
    ) {
      try {
        const fetched = await getShopCurrencyCode((clientDoc as any).shop, (clientDoc as any).accessToken);
        if (fetched) {
          currencyCode = fetched;
          await updateDocument('clients', clientDoc.id, { currencyCode: fetched });
        }
      } catch (e) {
        console.error('Error backfilling store currency code:', e);
      }
    }

    // The "previous visit" timestamp is just the value currently on the client
    // doc (the write below hasn't happened yet), so read it directly instead of
    // blocking the render on the write.
    let lastDashboardSeenAt: any = clientDoc?.lastDashboardSeenAt ?? null;
    let lastDashboardSeenAtIso: string | null = null;
    if (lastDashboardSeenAt) {
      try {
        lastDashboardSeenAtIso = timestampToDate(lastDashboardSeenAt).toISOString();
      } catch {}
    }
    // Record the visit + analytics fire-and-forget: these are a Firestore write
    // and a PostHog call that don't affect what this page renders, so awaiting
    // them only adds latency to every dashboard navigation.
    if (userEmail && clientDoc) {
      void recordDashboardSeen(userEmail, clientDoc).catch((e) =>
        console.error('Error recording dashboard seen:', e)
      );
      void trackEvent(userEmail, 'merchant_results_viewed', buildMerchantProperties(clientDoc)).catch(
        () => {}
      );
    }

    let userShop = shop;
    let playsStats = { totalPlays: 0, weeklyData: [] as { name: string; value: number; }[] };
    let revenueStats = { totalRevenue: 0, weeklyData: [] as { name: string; value: number; }[], upsellOrderCount: 0 };
    let orderStats = { totalOrders: 0, weeklyData: [] as { name: string; value: number; }[] };
    let gameDistribution = { totalGames: 0, gameDistribution: [] as { name: string; value: number; percentage: number; }[], mostPopularGame: null as { name: string; value: number; percentage: number; } | null, gameTypeCounts: {} as { [type: string]: number; } };
    let topClaimedRewards: any[] = [];
    let emailTrackingStats = { total: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, dropped: 0, spamReported: 0, unsubscribed: 0, failed: 0 };
    let conversionStats = { ordersWithPrizo: 0, ordersWithoutPrizo: 0, totalReturningOrders: 0, conversionRateWithPrizo: 0, conversionRateWithoutPrizo: 0, weeklyData: [] as { name: string; withPrizo: number; withoutPrizo: number; }[] };

    // If no shop in URL, try to get it from user's client data
    if (!userShop && userEmail) {
      try {
        const clientData = clientDoc || await getCachedClientData(userEmail);
        if (clientData && clientData.shop) {
          userShop = clientData.shop;
        }
        // Also try to get user name from client data if not already set
        if (clientData && clientData.name && !userName) {
          userName = clientData.name;
        }
      } catch (error) {
        console.error('Error getting user shop from client data:', error);
      }
    }

    // Fetch stats if we have userEmail - Using LRU Cache for performance 🚀
    let cachedData: any = null;
    if (userEmail) {

      try {
        // Get all dashboard data with caching. Passing the already-loaded
        // clientDoc lets every stat function reuse it instead of independently
        // re-reading the same client document from Firestore (~12 redundant
        // reads on a cache miss).
        const __tStats = Date.now();
        cachedData = await PerformanceCache.preloadDashboardData(userEmail, clientDoc);
        console.log(`[loader] dashboard preloadStats ${Date.now() - __tStats}ms`);

        // Get email tracking stats with caching
        const __tEmail = Date.now();
        const emailTrackingStatsResult = await PerformanceCache.cacheAPI(
          `email-stats:${userEmail}`,
          () => getEmailTrackingStats(userEmail),
          2 // 2 minutes cache for email stats
        );
        console.log(`[loader] dashboard emailStats ${Date.now() - __tEmail}ms`);

        // Assign cached results
        playsStats = cachedData.playsStats;
        revenueStats = cachedData.revenueStats;
        orderStats = cachedData.orderStats;
        gameDistribution = cachedData.gameDistribution;
        topClaimedRewards = cachedData.topClaimedRewards;
        conversionStats = cachedData.conversionStats;
        emailTrackingStats = emailTrackingStatsResult;

        // For the main "Plays" KPI card, use client.totalPlays instead of playsStats.totalPlays
        // playsStats.totalPlays is for the weekly chart (last 7 days)
        // client.totalPlays is the total plays count for the main KPI
        if (clientDoc && clientDoc.totalPlays !== undefined) {
          // Keep playsStats.totalPlays for the weekly chart, but we'll use client.totalPlays for the KPI card
        } else {
        }

      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    }

    console.log(`[loader] dashboard TOTAL ${Date.now() - __t0}ms`);
    return json({
      playsStats,
      widgetPlaysStats: cachedData?.widgetPlaysStats || { totalPlays: 0, weeklyData: [] },
      checkoutPlaysStats: cachedData?.checkoutPlaysStats || { totalPlays: 0, weeklyData: [] },
      widgetEmailStats: cachedData?.widgetEmailStats || { total: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, dropped: 0, spamReported: 0, unsubscribed: 0, failed: 0 },
      checkoutEmailStats: cachedData?.checkoutEmailStats || { total: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, dropped: 0, spamReported: 0, unsubscribed: 0, failed: 0 },
      checkoutRevenueStats: cachedData?.checkoutRevenueStats || { totalRevenue: 0, upsellOrderCount: 0, weeklyData: [] },
      widgetRevenueStats: cachedData?.widgetRevenueStats || { totalRevenue: 0, upsellOrderCount: 0, weeklyData: [] },
      checkoutConversionStats: cachedData?.checkoutConversionStats || { ordersWithPrizo: 0, conversionRateWithPrizo: 0, weeklyData: [] },
      widgetConversionStats: cachedData?.widgetConversionStats || { ordersWithPrizo: 0, conversionRateWithPrizo: 0, weeklyData: [] },
      revenueStats,
      orderStats,
      gameDistribution,
      topClaimedRewards,
      emailTrackingStats,
      conversionStats,
      currencyCode,
      shop: userShop,
      userName,
      userEmail,
      showInstallationPopup,
      showBillingConfirmationPopup,
      confirmationUrl,
      clientDoc, // Pass clientDoc to access totalPlays for the main Plays KPI
      lastDashboardSeenAt,
      lastDashboardSeenAtIso
    }, {
      headers: {
        "Cache-Control": "private, max-age=180, stale-while-revalidate=300", // Cache for 3 minutes with 5 min stale serving
        "Vary": "Cookie"
      }
    });
  } catch (error) {
    console.error('Error loading dashboard data:', error);
    return json({
      playsStats: { totalPlays: 0, weeklyData: [] },
      revenueStats: { totalRevenue: 0, weeklyData: [], upsellOrderCount: 0 },
      orderStats: { totalOrders: 0, weeklyData: [] },
      gameDistribution: { totalGames: 0, gameDistribution: [], mostPopularGame: null },
      topClaimedRewards: [],
      emailTrackingStats: { total: 0 },
      conversionStats: { ordersWithPrizo: 0, ordersWithoutPrizo: 0, totalReturningOrders: 0, conversionRateWithPrizo: 0, conversionRateWithoutPrizo: 0, weeklyData: [] },
      currencyCode: 'USD',
      shop: null,
      userName: "User",
      userEmail: null,
      showInstallationPopup: false,
      showBillingConfirmationPopup: false,
      confirmationUrl: null,
      clientDoc: null
    });
  }
};

export default function Index() {
  const { userEmail } = useLoaderData<typeof loader>();
  // Logged-out: render the public marketing landing page at "/".
  // Logged-in: render the merchant dashboard.
  if (!userEmail) {
    return <LandingPage />;
  }
  return <DashboardHome />;
}

// --- Prizo Creative redesign helpers ---------------------------------------

const STAT_ICON = {
  plays: "M6 4l14 8-14 8z",
  conversion: "M3 17l6-6 4 4 8-8M21 7v5M21 7h-5",
  revenue: "M12 3v18M16 7a4 4 0 0 0-4-2.5C9.5 4.5 8 6 8 8s2 3 4 3.5 4 1.5 4 3.5-1.5 3.5-4 3.5A4 4 0 0 1 8 17",
  star: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17l-5.2 2.6 1-5.8L3.5 9.7l5.9-.9z",
} as const;

const GAME_GRADS: [string, string][] = [
  ["#6d5cf8", "#5654f3"],
  ["#f472b6", "#db2777"],
  ["#34d399", "#059669"],
  ["#60a5fa", "#2563eb"],
  ["#fbbf24", "#ea7c1d"],
  ["#a78bfa", "#7c3aed"],
];
const REWARD_STUBS = ["#5b5cf6", "#ec4899", "#16a34a", "#f59e0b", "#7c3aed", "#2563eb"];

function rewardStubText(r: any): string {
  if (r?.type === "shipping") return "FREE";
  if (r?.type === "custom") return "🎁";
  const v = r?.percentage ?? r?.value ?? 0;
  if (r?.type === "amount_off_product" && r?.value && !r?.percentage) return `$${r.value}`;
  return `${v}%`;
}

function DashboardHome() {
  const loaderData = useLoaderData<typeof loader>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();

  const {
    playsStats,
    widgetPlaysStats,
    checkoutPlaysStats,
    checkoutRevenueStats,
    widgetRevenueStats,
    checkoutConversionStats,
    widgetConversionStats,
    orderStats,
    gameDistribution,
    topClaimedRewards,
    currencyCode,
    shop,
    userName,
    userEmail,
    confirmationUrl,
    clientDoc,
  } = loaderData;

  const [showOnboardingSuccess, setShowOnboardingSuccess] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showInstallationPopup, setShowInstallationPopup] = useState(false);
  const [showBillingConfirmationPopup, setShowBillingConfirmationPopup] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("onboarding") === "complete") {
      setShowOnboardingSuccess(true);
      window.history.replaceState({}, "", window.location.pathname);
      const tmo = setTimeout(() => setShowOnboardingSuccess(false), 5000);
      return () => clearTimeout(tmo);
    }
  }, []);

  useEffect(() => {
    if (loaderData.showInstallationPopup) setShowInstallationPopup(true);
  }, [loaderData.showInstallationPopup]);

  useEffect(() => {
    if (loaderData.showBillingConfirmationPopup) setShowBillingConfirmationPopup(true);
  }, [loaderData.showBillingConfirmationPopup]);

  const handleInstallationPopupClose = () => setShowInstallationPopup(false);
  const handleReinstallApp = () => {
    if (shop) window.location.href = `https://${shop}/admin/apps`;
    else navigate("/settings");
  };
  const handleBillingConfirmationPopupClose = () => setShowBillingConfirmationPopup(false);
  const handleAcceptBillingCharge = () => {
    if (confirmationUrl) window.location.href = confirmationUrl;
  };

  const handleRefresh = async () => {
    if (!userEmail || isRefreshing) return;
    setIsRefreshing(true);
    try {
      const response = await fetch("/api/clear-cache", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "user", userEmail }),
      });
      await response.json().catch(() => ({}));
    } catch (error) {
      console.error("❌ Error refreshing dashboard:", error);
    } finally {
      window.location.reload();
    }
  };

  // --- derived view data: per-channel metrics for the split cards ---
  const widgetMetrics = {
    plays: widgetPlaysStats?.totalPlays || 0,
    revenue: widgetRevenueStats?.totalRevenue || 0,
    conversion: widgetConversionStats?.conversionRateWithPrizo || 0,
    orders: widgetRevenueStats?.upsellOrderCount || 0,
  };
  const checkoutMetrics = {
    plays: checkoutPlaysStats?.totalPlays || 0,
    revenue: checkoutRevenueStats?.totalRevenue || 0,
    conversion: checkoutConversionStats?.conversionRateWithPrizo || 0,
    orders: checkoutRevenueStats?.upsellOrderCount || 0,
  };

  const games = (gameDistribution?.gameDistribution || []).map((g: { name: string; value: number }, i: number) => ({
    name: g.name,
    plays: `${Number(g.value || 0).toLocaleString()} ${t("dashboard.kpis.plays") || "plays"}`,
    grad: GAME_GRADS[i % GAME_GRADS.length],
  }));

  // Live rewards: prefer active rewards on the client doc; merge in claimed counts.
  const claimedByName: Record<string, number> = {};
  (topClaimedRewards || []).forEach((r: any) => {
    if (r?.name != null) claimedByName[r.name] = r.count;
  });
  const activeRewards = Array.isArray(clientDoc?.rewards)
    ? clientDoc!.rewards.filter((r: any) => (r?.status ? r.status === "active" : r?.isActive !== false))
    : [];
  let tickets = activeRewards.slice(0, 3).map((r: any, i: number) => ({
    pct: rewardStubText(r),
    code: r?.shopifyDiscountCode || r?.customDiscountCode || r?.name || "REWARD",
    claimed: `${claimedByName[r?.name] ?? 0} claimed`,
    color: REWARD_STUBS[i % REWARD_STUBS.length],
  }));
  if (tickets.length === 0 && (topClaimedRewards || []).length > 0) {
    tickets = (topClaimedRewards || []).slice(0, 3).map((r: any, i: number) => ({
      pct: "🎁",
      code: r.name,
      claimed: `${r.count} claimed`,
      color: REWARD_STUBS[i % REWARD_STUBS.length],
    }));
  }

  return (
    <div className="px-5 sm:px-8 lg:px-11 pt-8 pb-16 w-full">
      {/* Onboarding success toast */}
      {showOnboardingSuccess && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-2xl shadow-lift flex items-center gap-3">
            <CheckCircleIcon className="w-6 h-6 flex-none" />
            <div>
              <h3 className="text-sm font-bold">{t("dashboard.onboarding.success.title")}</h3>
              <p className="text-sm opacity-90">{t("dashboard.onboarding.success.message")}</p>
            </div>
            <button onClick={() => setShowOnboardingSuccess(false)} className="ml-2 text-white/80 hover:text-white">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-6 mb-7 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-muted">Performance</div>
          <h1 className="mt-1.5 font-display text-[34px] font-bold tracking-[-0.02em] text-ink">Your arcade is live.</h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={!userEmail || isRefreshing}
            title={t("dashboard.refresh")}
            className="w-11 h-11 flex items-center justify-center bg-white rounded-[14px] shadow-soft text-muted hover:text-brand transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-5 h-5 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Channel split: which channel drives plays & sales */}
      <ChannelSplitCards widget={widgetMetrics} checkout={checkoutMetrics} currencyCode={currencyCode} />

      {/* Games + live rewards */}
      <div className="grid gap-[26px] items-start lg:[grid-template-columns:1.3fr_1fr] mb-8">
        {/* Your games */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="m-0 font-display text-[19px] font-bold text-ink">Your games</h3>
            <Link to="/games" className="text-[13px] font-bold text-brand">Manage →</Link>
          </div>
          {games.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {games.map((g: { name: string; plays: string; grad: string[] }) => (
                <div key={g.name} className="bg-white rounded-[22px] p-5 shadow-soft flex items-center gap-[15px] transition-all hover:-translate-y-0.5 hover:shadow-lift">
                  <div
                    className="flex-none w-[52px] h-[52px] rounded-[16px] flex items-center justify-center"
                    style={{ background: `linear-gradient(140deg,${g.grad[0]},${g.grad[1]})`, boxShadow: `0 6px 14px ${g.grad[1]}55` }}
                  >
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3.5 7 12 3l8.5 4v10L12 21l-8.5-4zM3.5 7 12 11l8.5-4M12 11v10" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[15.5px] font-bold text-ink truncate">{g.name}</div>
                    <div className="text-[12.5px] text-faint font-semibold mt-0.5">{g.plays}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Link to="/games" className="block bg-white rounded-[22px] p-8 shadow-soft text-center">
              <div className="text-[15px] font-bold text-ink">No game plays yet</div>
              <div className="text-[13px] text-muted font-semibold mt-1">Launch a game to start collecting plays →</div>
            </Link>
          )}
        </div>

        {/* Live rewards */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="m-0 font-display text-[19px] font-bold text-ink">Live rewards</h3>
            <Link to="/rewards" className="text-[13px] font-bold text-brand">+ New</Link>
          </div>
          {tickets.length > 0 ? (
            <div className="flex flex-col gap-3.5">
              {tickets.map((r: { pct: string; code: string; claimed: string; color: string }, i: number) => (
                <div key={i} className="relative flex items-stretch bg-white rounded-[18px] overflow-hidden shadow-soft">
                  <div
                    className="w-24 flex-none flex flex-col items-center justify-center text-white px-2"
                    style={{ background: `linear-gradient(150deg,${r.color},${r.color}cc)` }}
                  >
                    <span className="font-display text-[22px] font-bold leading-none">{r.pct}</span>
                    <span className="text-[10.5px] font-bold opacity-85 tracking-[0.08em] mt-1.5">OFF</span>
                  </div>
                  <div className="flex-1 px-[18px] py-4 flex flex-col justify-center border-l-2 border-dashed border-[#e6e4f2]">
                    <div className="text-[11px] text-faint font-bold tracking-[0.06em]">CODE</div>
                    <div className="font-display text-[16px] font-bold tracking-[0.04em] text-ink truncate">{r.code}</div>
                    <div className="flex items-center gap-1.5 mt-2">
                      <span className="w-[7px] h-[7px] rounded-full bg-accent-green" />
                      <span className="text-[12px] text-muted font-semibold">{r.claimed}</span>
                    </div>
                  </div>
                  <div className="absolute left-[88px] -top-[9px] w-[18px] h-[18px] rounded-full bg-canvas" />
                  <div className="absolute left-[88px] -bottom-[9px] w-[18px] h-[18px] rounded-full bg-canvas" />
                </div>
              ))}
            </div>
          ) : (
            <Link to="/rewards" className="block bg-white rounded-[18px] p-8 shadow-soft text-center">
              <div className="text-[15px] font-bold text-ink">No rewards yet</div>
              <div className="text-[13px] text-muted font-semibold mt-1">Create a prize players can win →</div>
            </Link>
          )}
        </div>
      </div>

      {/* Daily plays chart */}
      <div className="bg-white rounded-cardlg p-6 shadow-soft mb-2">
        <h3 className="m-0 mb-4 font-display text-[19px] font-bold text-ink">Daily plays</h3>
        <DailyPlaysChart
          widgetData={widgetPlaysStats.weeklyData}
          checkoutData={checkoutPlaysStats.weeklyData}
          weeklyData={playsStats.weeklyData}
          isLoading={false}
        />
      </div>

      {/* Cache Monitor for Development */}
      <CacheMonitor userEmail={userEmail || undefined} />

      {/* Installation Issue Popup */}
      {showInstallationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">{t("dashboard.installation.title")}</h3>
              <p className="text-gray-600 text-center mb-6">{t("dashboard.installation.message")}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleReinstallApp}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                  {t("dashboard.installation.tryAgain")}
                </button>
                <button
                  onClick={handleInstallationPopupClose}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                >
                  {t("dashboard.installation.continueAnyway")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Confirmation Popup */}
      {showBillingConfirmationPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CreditCardIcon className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">{t("dashboard.billing.title")}</h3>
              <p className="text-gray-600 text-center mb-6">{t("dashboard.billing.message")}</p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAcceptBillingCharge}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 px-4 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <BanknotesIcon className="w-5 h-5" />
                  {t("dashboard.billing.acceptCharges")}
                </button>
                <button
                  onClick={handleBillingConfirmationPopupClose}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                >
                  {t("dashboard.billing.maybeLater")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
